import { getToken } from '../auth/api';
import { OWNER_ID, STAMP_METADATA_HEADER } from '../constants/constants';
import { failedToastMessage } from './commonUtils';
import { DOWNLOAD_URL } from './utilsConstants';
export { ATTACHMENT_DOWNLOAD_URL, DOWNLOAD_URL } from './utilsConstants';

const buildDownloadHeaders = (activeOrganizationAccount: string, stampDocument?: boolean): Headers => {
    const token = getToken();
    const headers = new Headers();
    headers.append('Authorization', token ? 'Bearer ' + token.token : '');
    if (activeOrganizationAccount) {
        headers.append(OWNER_ID, activeOrganizationAccount);
    }
    if (stampDocument ?? true) {
        headers.append(STAMP_METADATA_HEADER, 'true');
    }

    return headers;
};

/**
 * Reads a stored document as a blob instead of handing it to the browser to save.
 * Used when the document has to be passed on somewhere else - re-uploaded as an
 * attachment, for instance - rather than downloaded by the user.
 */
export const fetchDocumentBlob = async (
    documentId: string,
    activeOrganizationAccount: string,
    stampDocument?: boolean
): Promise<Blob> => {
    const response = await fetch(`${DOWNLOAD_URL}/${documentId}`, {
        method: 'GET',
        headers: buildDownloadHeaders(activeOrganizationAccount, stampDocument)
    });

    if (!response.ok) {
        throw new Error(`Failed to download document ${documentId}: ${response.status}`);
    }

    return response.blob();
};

export default function download(
    documentId: string,
    fileName: string,
    activeOrganizationAccount: string,
    target?: string,
    stampDocument?: boolean
): void {
    const url = `${DOWNLOAD_URL}/${documentId}`;
    const headers = buildDownloadHeaders(activeOrganizationAccount, stampDocument);

    fetch(url, {
        method: 'GET',
        headers: headers
    })
        .then((response) => {
            if (!response.ok) {
                throw new Error(`Download request failed with status ${response.status}`);
            }
            return response.blob();
        })
        .then((blob) => {
            // Create blob link to download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            if (target) {
                link.target = target;
            } else {
                link.setAttribute('download', fileName);
            }
            // Append to html link element page
            document.body.appendChild(link);

            // Start download
            link.click();

            // Clean up and remove the link
            link.parentNode.removeChild(link);
        })
        .catch(() => {
            failedToastMessage('common.download.failure');
        });
}
